import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Brain,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Target,
  Users,
  ArrowLeft,
  ArrowUpRight,
  IndianRupee
} from 'lucide-react'
import aiAnalyticsService from '../../services/aiAnalyticsService'

const getCurrentMonth = () => {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`
}

const formatCurrency = (amount, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0)

const priorityClass = (priority) => {
  if (priority === 'high') return 'priority-high'
  if (priority === 'medium') return 'priority-medium'
  return 'priority-low'
}

const DashboardAIAnalytics = () => {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [chatInput, setChatInput] = useState('')

  useEffect(() => {
    loadAnalytics()
  }, [])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      const res = await aiAnalyticsService.getAnalytics({ month: getCurrentMonth() })
      if (res?.success) {
        setData(res.data)
      } else if (!res?._errorHandled) {
        toast.error(res?.message || 'Failed to load AI analytics')
      }
    } catch (error) {
      console.error('AI analytics:', error)
      if (!error._errorHandled) {
        toast.error(error.response?.data?.message || 'Failed to load AI analytics')
      }
    } finally {
      setLoading(false)
    }
  }

  const insights = data?.chatInsights || []

  return (
    <div className="sales-dashboard">
      <div
        className="page-header d-flex flex-wrap justify-content-between align-items-start gap-3"
        style={{ textAlign: 'left' }}
      >
        <div className="page-title" style={{ textAlign: 'left', alignItems: 'flex-start' }}>
          <h1 style={{ textAlign: 'left', margin: '0 0 4px 0' }}>AI Analytics</h1>
          <p style={{ textAlign: 'left', margin: 0 }}>
            AI-powered insights derived from your live ERP data
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>
      </div>

      <div className="tab-content" style={{ display: 'block', visibility: 'visible' }}>
        <div className="ai-header-section">
          <div className="ai-header-content">
            <div className="ai-header-icon">
              <Brain size={24} />
            </div>
            <div className="ai-header-text">
              <h2>AI-Powered Analytics</h2>
              <p>Predictive analytics built from invoices, pipeline, burn, and customer health</p>
            </div>
          </div>
          <div className="ai-status">
            <div className="status-indicator active">
              <div className="status-dot"></div>
              <span>{loading ? 'Loading…' : 'Live data'}</span>
            </div>
          </div>
        </div>

        <div className="ai-insights-grid">
          <div className="ai-insight-card revenue-prediction">
            <div className="insight-header">
              <div className="insight-icon">
                <TrendingUp size={20} />
              </div>
              <div className="insight-title">Revenue Prediction</div>
              <div className="ai-badge">Gen AI</div>
            </div>
            <div className="insight-content">
              <div className="prediction-value">
                {loading ? '...' : formatCurrency(data?.revenuePrediction?.value, data?.currency || 'INR')}
              </div>
              <div className="prediction-period">{data?.revenuePrediction?.period || 'Next month forecast'}</div>
              <div className="confidence-score">
                <span className="confidence-label">Confidence:</span>
                <span className="confidence-value">{loading ? '—' : `${data?.revenuePrediction?.confidence || 0}%`}</span>
              </div>
              <div className="prediction-trend">
                {data?.revenuePrediction?.trendDirection === 'down' ? (
                  <TrendingDown size={14} />
                ) : (
                  <TrendingUp size={14} />
                )}
                <span>
                  {loading
                    ? '—'
                    : `${data?.revenuePrediction?.trendPct >= 0 ? '+' : ''}${data?.revenuePrediction?.trendPct || 0}% vs current month`}
                </span>
              </div>
            </div>
          </div>

          <div className="ai-insight-card churn-prediction">
            <div className="insight-header">
              <div className="insight-icon">
                <Users size={20} />
              </div>
              <div className="insight-title">Churn Risk Analysis</div>
              <div className="ai-badge">ML Model</div>
            </div>
            <div className="insight-content">
              <div className="churn-stats">
                <div className="churn-item high-risk">
                  <div className="churn-count">{loading ? '—' : data?.churnAnalysis?.highRisk ?? 0}</div>
                  <div className="churn-label">High Risk</div>
                </div>
                <div className="churn-item medium-risk">
                  <div className="churn-count">{loading ? '—' : data?.churnAnalysis?.mediumRisk ?? 0}</div>
                  <div className="churn-label">Medium Risk</div>
                </div>
                <div className="churn-item low-risk">
                  <div className="churn-count">{loading ? '—' : data?.churnAnalysis?.lowRisk ?? 0}</div>
                  <div className="churn-label">Low Risk</div>
                </div>
              </div>
              <div className="churn-recommendation">
                <span className="recommendation-text">
                  {loading ? 'Analysing customer payment patterns…' : data?.churnAnalysis?.recommendation}
                </span>
              </div>
            </div>
          </div>

          <div className="ai-insight-card smart-recommendations">
            <div className="insight-header">
              <div className="insight-icon">
                <Target size={20} />
              </div>
              <div className="insight-title">Smart Recommendations</div>
              <div className="ai-badge">Gen AI</div>
            </div>
            <div className="insight-content">
              <div className="recommendations-list">
                {loading ? (
                  <div style={{ padding: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>Generating recommendations…</div>
                ) : (
                  (data?.recommendations || []).map((item, index) => (
                    <div key={index} className={`recommendation-item ${priorityClass(item.priority)}`}>
                      <div className="recommendation-icon">💡</div>
                      <div className="recommendation-text">{item.text}</div>
                      <div className="recommendation-impact">{item.impact}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="ai-insight-card market-intelligence">
            <div className="insight-header">
              <div className="insight-icon">
                <BarChart3 size={20} />
              </div>
              <div className="insight-title">Market Intelligence</div>
              <div className="ai-badge">Pipeline</div>
            </div>
            <div className="insight-content">
              <div className="market-value">
                {loading ? '...' : formatCurrency(data?.marketIntelligence?.opportunityValue, data?.currency || 'INR')}
              </div>
              <div className="market-period">Open pipeline opportunity</div>
              <div className="market-insights">
                {(loading ? [] : data?.marketIntelligence?.insights || []).map((text, index) => (
                  <div key={index} className="market-trend neutral">
                    <div className="trend-icon">📊</div>
                    <div className="trend-text">{text}</div>
                  </div>
                ))}
              </div>
              <div className="market-opportunity">
                <span className="opportunity-text">
                  {loading
                    ? '—'
                    : `Pipeline deals closing in 30 days: ${data?.marketIntelligence?.pipelineDeals ?? 0}`}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="ai-chat-section">
          <div className="chat-header">
            <div className="chat-title">
              <Brain size={20} />
              <span>AI Business Assistant</span>
            </div>
            <div className="chat-status">
              <div className="status-dot active"></div>
              <span>Insights</span>
            </div>
          </div>
          <div className="chat-content">
            <div className="chat-messages">
              {loading ? (
                <div className="message ai-message">
                  <div className="message-avatar">🤖</div>
                  <div className="message-content">
                    <div className="message-text">Analysing your business data…</div>
                  </div>
                </div>
              ) : (
                insights.map((text, index) => (
                  <div key={index} className="message ai-message">
                    <div className="message-avatar">🤖</div>
                    <div className="message-content">
                      <div className="message-text">{text}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="chat-input">
              <div className="input-container">
                <IndianRupee size={16} className="input-icon" />
                <input
                  type="text"
                  placeholder="Ask about revenue, burn, or customer risk (INR)…"
                  className="chat-input-field"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  disabled
                />
                <button type="button" className="send-button" disabled title="Coming soon">
                  <ArrowUpRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="ai-metrics-section">
          <div className="metrics-header">
            <h3>Model Performance</h3>
            <p>Based on data freshness and forecast confidence</p>
          </div>
          <div className="ai-metrics-grid">
            <div className="metric-item">
              <div className="metric-label">Prediction Confidence</div>
              <div className="metric-value">
                {loading ? '—' : `${data?.modelPerformance?.predictionAccuracy || 0}%`}
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-label">Data Sources</div>
              <div className="metric-value">{loading ? '—' : data?.modelPerformance?.dataSourcesUsed ?? 0}</div>
            </div>
            <div className="metric-item">
              <div className="metric-label">Insights Generated</div>
              <div className="metric-value">{loading ? '—' : data?.modelPerformance?.insightsGenerated ?? 0}</div>
            </div>
            <div className="metric-item">
              <div className="metric-label">Currency</div>
              <div className="metric-value">{data?.currency || 'INR'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardAIAnalytics
